import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { notifyNativeShell } from "../lib/nativeBridge";

const STORAGE_KEY = "angelsquad.member";

/**
 * Capture a Hub-signed handoff token at module load — BEFORE React mounts
 * or any <Navigate> strips the URL. The Hub redirects paid members to:
 *   https://myangelsquad.com/?token=<JWT>
 * We read it, clean the URL so the token doesn't linger in the address bar
 * or browser history, then verify with the backend on the first mount.
 */
const HUB_HANDOFF_TOKEN = (() => {
    try {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");
        if (token) {
            const url = new URL(window.location.href);
            url.searchParams.delete("token");
            window.history.replaceState({}, "", url.toString());
            return token;
        }
    } catch { /* ignore */ }
    return null;
})();

function readStored() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeStored(member) {
    try {
        if (member) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(member));
        else window.localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
}

const MemberContext = createContext(null);

export function MemberProvider({ children }) {
    const [member, setMember] = useState(null);
    const [identifyError, setIdentifyError] = useState("");
    // Make sure the `bridge_ready` handshake only fires once per page load,
    // even if the member object is re-set later in the session.
    const bridgeReadySentRef = useRef(false);

    const handoff = useCallback(async (token) => {
        try {
            const { data } = await api.post("/members/handoff", { token });
            writeStored(data);
            setMember(data);
            return { ok: true, member: data };
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setIdentifyError(msg);
            // Wipe any stale stored identity — bad handoff means we don't trust it.
            writeStored(null);
            setMember(false);
            return { ok: false, error: msg };
        }
    }, []);

    const clear = useCallback(() => {
        writeStored(null);
        setMember(false);
    }, []);

    useEffect(() => {
        if (HUB_HANDOFF_TOKEN) {
            handoff(HUB_HANDOFF_TOKEN);
            return;
        }
        const stored = readStored();
        if (stored?.memberId) setMember(stored);
        else setMember(false);
    }, [handoff]);

    // Once we have an identified member, fetch a snapshot of their current
    // selection + cooldown and broadcast a one-shot `bridge_ready` event to
    // the native shell. This lets the Inspirations app reconcile its local
    // state on every WebView open without polling — useful when the member
    // changed their Angel on another device.
    useEffect(() => {
        if (!member || !member.memberId) return;
        if (bridgeReadySentRef.current) return;
        bridgeReadySentRef.current = true;

        let cancelled = false;
        (async () => {
            let currentAngel = null;
            let nextChangeAllowedAt = null;
            try {
                const { data } = await api.get("/selections/me");
                currentAngel = data?.selection?.angelNumber || null;
                nextChangeAllowedAt = data?.nextChangeAllowedAt || null;
            } catch {
                // Bridge handshake should never block — emit with what we have.
            }
            if (cancelled) return;
            notifyNativeShell({
                type: "bridge_ready",
                memberId: member.memberId,
                currentAngel,
                nextChangeAllowedAt,
                source: "myangelsquad.com",
                emittedAt: new Date().toISOString(),
            });

            // If the member's chosen Angel gained a fresh design (<7 days),
            // tell the shell once per design so it can badge/notify natively.
            if (!currentAngel) return;
            try {
                const { data: angel } = await api.get(`/angels/${currentAngel}`);
                const designs = angel?.designs || [];
                const newest = designs.length > 1 ? designs[designs.length - 1] : null;
                const isFresh = newest
                    && Date.now() - new Date(newest.uploadedAt).getTime() < 7 * 24 * 3600 * 1000;
                if (cancelled || !isFresh) return;
                const dedupeKey = "angelsquad.nativeBridge.lastNewDesignId";
                if (window.localStorage.getItem(dedupeKey) === newest.id) return;
                window.localStorage.setItem(dedupeKey, newest.id);
                notifyNativeShell({
                    type: "new_design",
                    angelNumber: currentAngel,
                    designId: newest.id,
                    label: newest.label,
                    uploadedAt: newest.uploadedAt,
                    designCount: designs.length,
                    notification: {
                        title: `Angel ${currentAngel} has a new design`,
                        body: `${newest.label} just landed for your Angel — open the portal to see it and pick your favourite.`,
                    },
                    source: "myangelsquad.com",
                    emittedAt: new Date().toISOString(),
                });
            } catch {
                // best-effort — never block the portal on bridge extras
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [member]);

    return (
        <MemberContext.Provider value={{ member, identifyError, handoff, clear }}>
            {children}
        </MemberContext.Provider>
    );
}

export function useMember() {
    return useContext(MemberContext);
}
